import React from 'react';
import { ThemeConfig } from '@/types';
import { themePresets, fontOptions } from '@/themes/presets';

interface ThemeEditorProps {
  theme: ThemeConfig;
  onChange: (theme: ThemeConfig) => void;
}

export function ThemeEditor({ theme, onChange }: ThemeEditorProps) {
  const handlePresetSelect = (presetId: string) => {
    const preset = themePresets.find((p) => p.id === presetId);
    if (preset) {
      onChange({
        preset: preset.id,
        primary_color: preset.primary_color,
        secondary_color: preset.secondary_color,
        accent_color: preset.accent_color,
        font_family: preset.font_family,
        background: preset.background,
      });
    }
  };

  const handleColorChange = (key: keyof ThemeConfig, value: string) => {
    onChange({ ...theme, [key]: value, preset: 'custom' });
  };

  return (
    <div className="space-y-6">
      {/* Preset Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Theme Preset
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {themePresets.map((preset) => (
            <button
              key={preset.id}
              onClick={() => handlePresetSelect(preset.id)}
              className={`p-3 rounded-lg border-2 text-left transition-all duration-200 hover:scale-[1.02] ${
                theme.preset === preset.id
                  ? 'border-violet-500 ring-2 ring-violet-200'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex gap-1 mb-2">
                <div
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: preset.primary_color }}
                />
                <div
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: preset.secondary_color }}
                />
                <div
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: preset.accent_color }}
                />
              </div>
              <p className="text-sm font-medium text-gray-900">{preset.name}</p>
              <p className="text-xs text-gray-500 mt-0.5">{preset.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Color Pickers */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Primary Color
          </label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={theme.primary_color}
              onChange={(e) => handleColorChange('primary_color', e.target.value)}
              className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer"
            />
            <input
              type="text"
              value={theme.primary_color}
              onChange={(e) => handleColorChange('primary_color', e.target.value)}
              className="input-field text-sm"
              placeholder="#000000"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Secondary Color
          </label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={theme.secondary_color}
              onChange={(e) => handleColorChange('secondary_color', e.target.value)}
              className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer"
            />
            <input
              type="text"
              value={theme.secondary_color}
              onChange={(e) => handleColorChange('secondary_color', e.target.value)}
              className="input-field text-sm"
              placeholder="#000000"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Accent Color
          </label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={theme.accent_color}
              onChange={(e) => handleColorChange('accent_color', e.target.value)}
              className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer"
            />
            <input
              type="text"
              value={theme.accent_color}
              onChange={(e) => handleColorChange('accent_color', e.target.value)}
              className="input-field text-sm"
              placeholder="#000000"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Background Color
          </label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={theme.background}
              onChange={(e) => handleColorChange('background', e.target.value)}
              className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer"
            />
            <input
              type="text"
              value={theme.background}
              onChange={(e) => handleColorChange('background', e.target.value)}
              className="input-field text-sm"
              placeholder="#FFFFFF"
            />
          </div>
        </div>
      </div>

      {/* Font Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Font Family
        </label>
        <select
          value={theme.font_family}
          onChange={(e) => onChange({ ...theme, font_family: e.target.value })}
          className="input-field"
        >
          {fontOptions.map((font) => (
            <option key={font.value} value={font.value}>
              {font.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
